import { API_ROOT } from '../config'

const FLUSH_INTERVAL_MS = 30000 // 30 seconds
const BATCH_SIZE = 20           // flush early if queue grows this large

/**
 * Tracker — lightweight singleton for collecting user activity events
 * (page views, clicks, logins) and sending them to the API in batches.
 *
 * Usage:
 *   import { tracker } from '../utils/tracker'
 *   tracker.track('CLICK', '/machines', 'Open Machine Detail')
 *
 * Identity is set automatically via setUser() when auth state changes
 * (wired in the Redux tracking middleware in store/index.js).
 */
class Tracker {
  constructor() {
    this._queue = []
    this._user = null

    // Periodic flush
    this._timer = setInterval(() => this._flush(), FLUSH_INTERVAL_MS)

    // Flush remaining events when the tab/window is closing.
    // fetch with keepalive:true lets the browser complete the request
    // even after the page unloads, while still carrying the Auth header.
    window.addEventListener('beforeunload', () => this._flush())
  }

  /**
   * Called by the Redux tracking middleware whenever auth state changes.
   * Pass null to clear (on logout).
   */
  setUser(user) {
    this._user = user
  }

  /**
   * Queue a single event.
   *
   * @param {string} action  - 'PAGE_VIEW' | 'CLICK' | 'LOGIN' | 'LOGOUT'
   * @param {string} page    - route path, e.g. '/machines'
   * @param {string} [label] - human-readable label (page title, button name…)
   * @param {object} [meta]  - optional small context object — keep it tiny
   */
  track(action, page, label = '', meta = null) {
    if (!this._user) return

    const event = {
      ts: new Date().toISOString(),
      // uid/name/role/type are also sent for local reference, but the
      // server always overwrites them from the JWT — they are NOT trusted.
      uid:    this._user.id,
      name:   this._user.fullname,
      role:   this._user.role,
      type:   this._user.type || '',
      action,
      page:   page || window.location.pathname,
      label:  label || ''
    }

    if (meta && typeof meta === 'object') event.meta = meta

    this._queue.push(event)

    if (this._queue.length >= BATCH_SIZE) {
      this._flush()
    }
  }

  _flush() {
    if (this._queue.length === 0) return

    const token = localStorage.getItem('token')
    if (!token) return

    // Drain the queue atomically so concurrent flushes don't double-send
    const batch = this._queue.splice(0)

    fetch(`${API_ROOT}activitylogs/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(batch),
      // keepalive allows the request to outlive the page — essential for
      // the beforeunload flush. Max payload is ~64 KB which is plenty for
      // BATCH_SIZE events.
      keepalive: true
    }).catch(() => {
      // Fire-and-forget: silently discard on network error.
      // Analytics should never break the app.
    })
  }
}

export const tracker = new Tracker()
